import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Briefcase, GraduationCap, Building2, ArrowRight, ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: authLogin, user } = useAuth();
  
  const [mode, setMode] = useState('signin');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Get user type from URL params
  const userTypeParam = searchParams.get('type') || 'individual';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    user_type: userTypeParam,
    organization: '',
  });

  useEffect(() => {
    if (user) {
      redirectAfterAuth(user);
    }
  }, [user]);

  const redirectAfterAuth = (userData) => {
    if (userData.is_admin) {
      navigate('/admin');
    } else {
      navigate('/instructions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === 'signin') {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password,
        });
        authLogin(response.data.token, response.data.user);
        toast.success('Welcome back!');
        redirectAfterAuth(response.data.user);
      } else {
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          user_type: formData.user_type,
          organization: formData.organization || null,
        });
        authLogin(response.data.token, response.data.user);
        toast.success('Account created successfully!');
        redirectAfterAuth(response.data.user);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'An error occurred';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeConfig = () => {
    const configs = {
      individual: {
        icon: Briefcase,
        title: 'Professional',
        subtitle: 'Executive Assessment',
        color: '#CC5500',
        description: 'For executives and professionals seeking board-ready leadership intelligence.',
      },
      student: {
        icon: GraduationCap,
        title: 'Student',
        subtitle: 'Career Discovery',
        color: '#C5A065',
        description: 'For students and early-career professionals discovering their leadership path.',
      },
      corporate: {
        icon: Building2,
        title: 'Corporate',
        subtitle: 'Enterprise Assessment',
        color: '#4A90A4',
        description: 'For organizations assessing executive talent and leadership teams.',
      },
    };
    return configs[formData.user_type] || configs.individual;
  };

  const config = getUserTypeConfig();
  const IconComponent = config.icon;

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-4" data-testid="auth-page">
      {/* Background effects */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a1a] to-[#0D0D0D]" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#CC5500]/10 rounded-full filter blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#C5A065]/10 rounded-full filter blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Back to home */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-white">
            Lead<span className="text-[#CC5500]">Economics</span>™
          </h1>
          <p className="text-sm text-gray-500 mt-1">Premium Leadership Intelligence</p>
        </div>

        {/* User Type Badge */}
        <div className="flex justify-center mb-6">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
            style={{ 
              backgroundColor: `${config.color}15`,
              borderColor: `${config.color}30`,
            }}
          >
            <IconComponent className="h-4 w-4" style={{ color: config.color }} />
            <span className="text-sm font-medium" style={{ color: config.color }}>
              {config.title} {config.subtitle}
            </span>
          </div>
        </div>

        {/* Auth Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <Tabs value={mode} onValueChange={setMode} className="space-y-6">
            <TabsList className="w-full bg-white/5 border border-white/10 p-1 rounded-xl">
              <TabsTrigger 
                value="signin" 
                className="flex-1 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg"
                data-testid="signin-tab"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="signup" 
                className="flex-1 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg"
                data-testid="signup-tab"
              >
                Create Account
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <div>
                  <Label htmlFor="full_name" className="text-gray-400 text-sm">Full Name</Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Your full name"
                    required={mode === 'signup'}
                    className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#CC5500]"
                    data-testid="fullname-input"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-gray-400 text-sm">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#CC5500]"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-400 text-sm">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#CC5500] pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {mode === 'signup' && formData.user_type === 'corporate' && (
                <div>
                  <Label htmlFor="organization" className="text-gray-400 text-sm">Organization</Label>
                  <Input
                    id="organization"
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    placeholder="Your company name"
                    className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#CC5500]"
                    data-testid="organization-input"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:from-[#CC5500]/90 hover:to-[#C5A065]/90 text-white py-6 mt-4"
                data-testid="auth-submit-button"
              >
                {loading ? 'Processing...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </Tabs>

          {/* User Type Selector (for signup) */}
          {mode === 'signup' && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-sm text-gray-500 mb-3">Select your assessment type:</p>
              <div className="grid grid-cols-3 gap-2">
                {['individual', 'student', 'corporate'].map((type) => {
                  const typeConfig = {
                    individual: { icon: Briefcase, label: 'Professional', color: '#CC5500' },
                    student: { icon: GraduationCap, label: 'Student', color: '#C5A065' },
                    corporate: { icon: Building2, label: 'Corporate', color: '#4A90A4' },
                  }[type];
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({ ...formData, user_type: type })}
                      className={`p-3 rounded-xl border transition-all ${
                        formData.user_type === type
                          ? 'border-opacity-100 bg-opacity-10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      style={{
                        borderColor: formData.user_type === type ? typeConfig.color : undefined,
                        backgroundColor: formData.user_type === type ? `${typeConfig.color}15` : undefined,
                      }}
                      data-testid={`user-type-${type}`}
                    >
                      <TypeIcon 
                        className="h-5 w-5 mx-auto mb-1" 
                        style={{ color: formData.user_type === type ? typeConfig.color : '#666' }}
                      />
                      <p className="text-xs" style={{ color: formData.user_type === type ? typeConfig.color : '#888' }}>
                        {typeConfig.label}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-600 mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};
